import React, { useCallback, useMemo } from 'react';
import { reactHookForm } from '@yola/ws-ui';
import PropTypes from 'prop-types';
import DifmBannerModal from '../components/difm-banner-modal';
import DifmBannerSurvey from '../components/difm-banner-survey';
import { serviceNameValues, fieldNames } from '../constants/common';
import servicesOptions from '../constants/services-options';

const { useForm } = reactHookForm;

const modalCaptions = {
  back: 'Back',
  next: 'Continue',
};

const getInitialData = (values) => {
  switch (values.serviceName) {
    case serviceNameValues.BUILD_YOUR_DREAM_SITE:
      return {
        [fieldNames.BUSINESS_AND_PURPOSE]: values[fieldNames.BUSINESS_AND_PURPOSE] || '',
        [fieldNames.DOES_HAS_CONTENT]: values[fieldNames.DOES_HAS_CONTENT] || '',
        [fieldNames.CONTENT_TYPE]: values[fieldNames.CONTENT_TYPE] || [],
        [fieldNames.OTHER_CONTENT_TYPE]: values[fieldNames.OTHER_CONTENT_TYPE] || '',
        [fieldNames.FEATURE_AND_FUNCTIONALITY]: values[fieldNames.FEATURE_AND_FUNCTIONALITY] || '',
      };
    case serviceNameValues.REVAMP_YOUR_WEBSITE:
      return {
        [fieldNames.BUSINESS_AND_PURPOSE]: values[fieldNames.BUSINESS_AND_PURPOSE] || '',
        [fieldNames.USER_IMPROVEMENTS]: values[fieldNames.USER_IMPROVEMENTS] || '',
      };
    case serviceNameValues.MIGRATE_YOUR_SITE_TO_YOLA:
      return {
        [fieldNames.OLD_WEBSITE_URL]: values[fieldNames.OLD_WEBSITE_URL] || '',
        [fieldNames.OLD_WEBSITE_PLATFORM]: values[fieldNames.OLD_WEBSITE_PLATFORM] || '',
        [fieldNames.OTHER_OLD_WEBSITE_PLATFORM]:
          values[fieldNames.OTHER_OLD_WEBSITE_PLATFORM] || '',
        [fieldNames.MIGRATION_REASON]: values[fieldNames.MIGRATION_REASON] || [],
        [fieldNames.OTHER_MIGRATION_REASON]: values[fieldNames.OTHER_MIGRATION_REASON] || '',
        [fieldNames.USER_IMPROVEMENTS]: values[fieldNames.USER_IMPROVEMENTS] || '',
      };
    default:
      return null;
  }
};

const DifmBannerSurveyContainer = ({ values, onBack, onClose, onNext }) => {
  const { serviceName, selectedPackage } = values;

  const defaultValues = useMemo(() => getInitialData(values), [values]);

  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: 'onTouched',
  });

  const handleBack = useCallback(() => {
    const formValues = getValues();
    onBack({
      serviceName,
      selectedPackage,
      ...formValues,
    });
  }, [getValues, onBack, serviceName, selectedPackage]);

  const handleNext = useCallback(() => {
    const formValues = getValues();

    onNext({
      serviceName,
      selectedPackage,
      ...formValues,
    });
  }, [getValues, onNext, serviceName, selectedPackage]);

  const title = useMemo(
    () => servicesOptions.find(({ value }) => value === values.serviceName).title,
    [values]
  );

  return (
    <form onSubmit={handleSubmit(handleNext)}>
      <DifmBannerModal
        onNext={handleSubmit(handleNext)}
        isNextDisabled={Boolean(Object.keys(errors).length)}
        onBack={handleBack}
        onClose={onClose}
        captions={{ ...modalCaptions, title }}
      >
        <DifmBannerSurvey
          serviceName={values.serviceName}
          getValues={getValues}
          control={control}
        />
      </DifmBannerModal>
    </form>
  );
};

DifmBannerSurveyContainer.propTypes = {
  values: PropTypes.shape({
    serviceName: PropTypes.string.isRequired,
    selectedPackage: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default DifmBannerSurveyContainer;
